import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Typography, Stack, FormControlLabel, Checkbox, Card, CardContent, Paper, Divider } from '@mui/material';
import { captureException } from '@sentry/react';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';

import { db } from '../../firebase-config';
import { useConsultTime, useAuth } from '../../hooks';
import { SkeletonWrapper } from './SkeletonWrapper';
import { ConfirmationModalWrapper } from '../ConfirmationModalWrapper';
import { FORM_CATEGORIES_INVERTED, CONSULT_TRANSLATOR_INVERTED, CONSULT_TYPE } from '../../utils';

/**
 * @param {object} param
 * @param {FormCategory} param.formCategory
 * @param {import('../../hooks/useConsultTime').ConsultTimeDisplayText} param.consultTime
 * @param {string[]} [param.consultIds]
 * @returns {Record<string, Timestamp|string|string[]>|null} - The key to update or null if formCategory is invalid.
 */
const getConsultTimeUpdate = ({ formCategory, consultTime, consultIds }) => {
  const formKey = FORM_CATEGORIES_INVERTED[formCategory];

  if (!formKey) {
    return null;
  }

  const healthCategories =
    formCategory === CONSULT_TYPE.CHECKUP
      ? consultIds?.map((id) => FORM_CATEGORIES_INVERTED?.[CONSULT_TRANSLATOR_INVERTED?.[id]])
      : null;

  return {
    [`triageConsultTimeAcknowledged.${formKey}.acceptedAt`]: Timestamp.now(),
    // We're storing the consult time during the time of acknowledgment, just in case.
    [`triageConsultTimeAcknowledged.${formKey}.consultTime`]: consultTime.DOCTOR || '',
    [`triageConsultTimeAcknowledged.${formKey}.consultTimeNurse`]: consultTime.NURSE || '',
    ...(healthCategories && { [`triageConsultTimeAcknowledged.${formKey}.healthCategories`]: healthCategories }),
  };
};

/**
 * @param {object} props
 * @param {FormCategory} props.formCategory
 * @param {() => Promise<void> | void} props.onConfirm
 * @param {React.ReactElement} props.children
 * @param {string[]} [props.consultIds]
 * @returns {JSX.Element}
 */
export const ConsultationReviewModal = ({ formCategory, consultIds, onConfirm, children }) => {
  const { user } = useAuth();
  const { consultTime, isLoading, isTriageAcknowledged, isNicotineCheckup } = useConsultTime({
    consultIds,
    formCategory,
    patientId: user?.uid || '',
    shouldGetPatientConsultTime: true,
  });
  const [accepted, setAccepted] = useState(isTriageAcknowledged);

  const onProceedWithPayment = async () => {
    if (!consultTime.DOCTOR && !consultTime.NURSE) {
      onConfirm?.();

      return;
    }

    const userId = user?.uid;
    toast.promise(
      async () => {
        if (!userId) {
          throw new Error('No user ID found');
        }

        const consultTimeUpdate = getConsultTimeUpdate({ formCategory, consultIds, consultTime });

        if (!consultTimeUpdate) {
          throw new Error(`Invalid form category: ${formCategory}`);
        }

        await updateDoc(doc(db, 'patients', userId), consultTimeUpdate);
        await onConfirm?.();
      },
      {
        loading: 'Opening payment gateway...',
        error: (error) => {
          captureException(error, {
            tags: { formCategory, issueIn: 'handleScriptOnlyConfig', userId },
            extra: { consultTime, consultIds },
          });

          return `There was an error when proceeding to payment. Please try again or contact support if the issue persists.`;
        },
      },
    );
  };

  const consultDescription = consultTime.DOCTOR || consultTime.NURSE || null;

  useEffect(() => {
    setAccepted(isTriageAcknowledged);
  }, [isTriageAcknowledged]);

  return (
    <ConfirmationModalWrapper
      title="Consultation Review Timeline"
      description={
        <Stack spacing={2} direction="column" sx={{ p: 3 }}>
          <Typography variant="body1">
            Once you have paid, your consultation will be reviewed by our clinical team
          </Typography>
          {isNicotineCheckup && (
            <Typography variant="body1">A doctor will call you within 24 hours to conduct your checkup</Typography>
          )}
          {consultDescription && !isNicotineCheckup && (
            <>
              <Typography variant="body1">
                {consultTime.DOCTOR
                  ? 'Please note the total process from payment to doctor consultation is currently:'
                  : 'You will hear from a member of our nursing team within:'}
              </Typography>
              <SkeletonWrapper isLoading={isLoading} width="100%">
                <Card variant="outlined" component={Paper} sx={{ borderRadius: 3 }}>
                  <CardContent
                    spacing={3}
                    direction="row"
                    component={Stack}
                    sx={{ alignItems: 'center', '&:last-child': { paddingBottom: 2 } }}
                  >
                    <HourglassTopIcon color="primary" sx={{ fontSize: 40 }} />
                    <Typography variant="h6" width="100%">
                      {consultDescription}
                    </Typography>
                  </CardContent>
                </Card>
                <Divider />
                <FormControlLabel
                  sx={{ gap: 2, pl: 6 }}
                  control={
                    <Checkbox color="primary" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                  }
                  label={
                    <Typography variant="body1" align="center">
                      I accept and continue.
                    </Typography>
                  }
                />
                <Divider />
              </SkeletonWrapper>
            </>
          )}
        </Stack>
      }
      confirmColour="primary"
      titleIcon={ScheduleIcon}
      onConfirm={onProceedWithPayment}
      confirmText="Proceed with payment"
      disableConfirm={!accepted && Boolean(consultTime.DOCTOR)}
    >
      {children}
    </ConfirmationModalWrapper>
  );
};
